// ============================================================
// 天水數位店長 — 請假頁面
// 員工送假單 + 查看餘額 + 歷史
// ============================================================

import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../stores/authStore';
import { api } from '../lib/api';

interface LeaveBalance {
  leaveType: string;
  entitled: number;
  used: number;
  remaining: number;
}

interface LeaveEstimateDay {
  date: string;
  hours: number;
  source: 'schedule' | 'rest' | 'default';
  shiftName?: string;
}

interface LeaveEstimate {
  hours: number;
  days: LeaveEstimateDay[];
}

interface LeaveRecord {
  id: string;
  leaveType: string;
  startDate: string;
  endDate: string;
  hours: number;
  status: string;
  reason?: string;
}

const LEAVE_TYPES = [
  { value: 'ANNUAL', label: '特休' },
  { value: 'SICK', label: '病假' },
  { value: 'PERSONAL', label: '事假' },
  { value: 'MENSTRUAL', label: '生理假' },
  { value: 'FAMILY_CARE', label: '家庭照顧假' },
  { value: 'OFFICIAL', label: '公假' },
  { value: 'UNPAID', label: '無薪假' },
  { value: 'BEREAVEMENT', label: '喪假' },
];

const leaveTypeLabels: Record<string, string> = Object.fromEntries(LEAVE_TYPES.map((t) => [t.value, t.label]));

const statusMap: Record<string, { label: string; style: string }> = {
  PENDING: { label: '審核中', style: 'bg-smoke-100 text-smoke-500' },
  APPROVED: { label: '已核准', style: 'bg-sage-100 text-sage-600' },
  REJECTED: { label: '已駁回', style: 'bg-rose-100 text-rose-500' },
  CANCELLED: { label: '已取消', style: 'bg-morandi-100 text-morandi-400' },
};

export function LeavePage() {
  const { user } = useAuthStore();
  const [showForm, setShowForm] = useState(false);
  const [leaveType, setLeaveType] = useState('ANNUAL');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [estimate, setEstimate] = useState<LeaveEstimate | null>(null);
  const [estimating, setEstimating] = useState(false);
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  const [balances, setBalances] = useState<LeaveBalance[]>([]);
  const [records, setRecords] = useState<LeaveRecord[]>([]);

  const fetchData = async () => {
    if (!user) return;
    try {
      const [balRes, recRes] = await Promise.all([
        api.get<{ balances: LeaveBalance[] }>(`/leaves/balance/${user.id}`),
        api.get<{ leaves: LeaveRecord[] }>('/leaves'),
      ]);
      setBalances(balRes.balances);
      setRecords(recRes.leaves);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    fetchData();
  }, [user]);

  // 起訖日選好就向後端估算時數。
  // 時數由後端依當天的班表算（4 小時的班就是 4 小時，休息日 0），
  // 前端只負責顯示 —— 送出時也不再自己算。
  useEffect(() => {
    if (!startDate || !endDate || endDate < startDate) {
      setEstimate(null);
      return;
    }
    let cancelled = false;
    setEstimating(true);
    api.get<LeaveEstimate>(`/leaves/estimate?startDate=${startDate}&endDate=${endDate}`)
      .then((data) => { if (!cancelled) setEstimate(data); })
      .catch(() => { if (!cancelled) setEstimate(null); })
      .finally(() => { if (!cancelled) setEstimating(false); });
    return () => { cancelled = true; };
  }, [startDate, endDate]);

  const handleCancel = async (leave: LeaveRecord) => {
    if (!confirm(`確定要取消 ${leave.startDate.slice(0, 10)} 的假單嗎？`)) return;
    setCancellingId(leave.id);
    try {
      await api.put(`/leaves/${leave.id}/cancel`);
      await fetchData();
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setCancellingId(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // 時數由後端依排班計算，這裡不送 hours
      await api.post('/leaves', { leaveType, startDate, endDate, reason: reason || undefined });
      setShowForm(false);
      setLeaveType('ANNUAL');
      setStartDate('');
      setEndDate('');
      setReason('');
      setEstimate(null);
      fetchData();
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="h-page flex flex-col bg-morandi-50 p-4">
      <header className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-morandi-800 tracking-tight">請假</h1>
        <button onClick={() => setShowForm(!showForm)}
          className="px-4 py-2 bg-smoke-500 text-white rounded-btn font-medium hover:bg-smoke-600 transition-colors min-h-touch">
          {showForm ? '取消' : '申請請假'}
        </button>
      </header>

      {/* 餘額 */}
      <div className="grid grid-cols-4 gap-3 mb-4">
        {balances.length > 0 ? balances.map((b) => (
          <div key={b.leaveType} className="bg-white rounded-card shadow-card p-3 text-center">
            <div className="text-xs text-morandi-400">{leaveTypeLabels[b.leaveType] ?? b.leaveType}</div>
            <div className="text-xl font-mono font-bold text-morandi-800 mt-1">{b.remaining}</div>
            <div className="text-xs text-morandi-300 mt-0.5">已用 {b.used} / {b.entitled} 天</div>
          </div>
        )) : (
          <div className="col-span-4 bg-white rounded-card shadow-card p-3 text-center text-morandi-300 text-sm">
            載入餘額中...
          </div>
        )}
      </div>

      {/* 表單 */}
      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white rounded-card shadow-card p-4 mb-4 animate-slide-up">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-morandi-400 mb-1.5">假別</label>
              <select value={leaveType} onChange={(e) => setLeaveType(e.target.value)}
                className="w-full px-3 py-2.5 bg-morandi-50 border border-morandi-200 rounded-btn text-sm text-morandi-700 min-h-touch">
                {LEAVE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div />
            <div>
              <label className="block text-xs text-morandi-400 mb-1.5">開始日期</label>
              <input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); if (!endDate) setEndDate(e.target.value); }}
                required className="w-full px-3 py-2.5 bg-morandi-50 border border-morandi-200 rounded-btn text-sm text-morandi-700 min-h-touch" />
            </div>
            <div>
              <label className="block text-xs text-morandi-400 mb-1.5">結束日期</label>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                required className="w-full px-3 py-2.5 bg-morandi-50 border border-morandi-200 rounded-btn text-sm text-morandi-700 min-h-touch" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs text-morandi-400 mb-1.5">原因（選填）</label>
              <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
                className="w-full px-3 py-2.5 bg-morandi-50 border border-morandi-200 rounded-btn text-sm text-morandi-700 resize-none" />
            </div>
          </div>

          {/* 預估時數：讓員工在送出前就知道會扣掉幾小時 */}
          {startDate && endDate && (
            <div className="mt-4 bg-morandi-50 border border-morandi-200 rounded-btn p-3">
              {estimating ? (
                <div className="text-sm text-morandi-400">計算中…</div>
              ) : estimate ? (
                <>
                  <div className="text-sm text-morandi-700">
                    預估 <span className="font-mono font-bold text-smoke-600">{estimate.hours}</span> 小時
                    <span className="text-morandi-400">（依你的班表）</span>
                  </div>
                  <div className="mt-2 space-y-1">
                    {estimate.days.map((d) => (
                      <div key={d.date} className="flex items-center justify-between text-xs">
                        <span className="text-morandi-500">
                          {d.date.slice(5)}
                          {d.shiftName && <span className="text-morandi-300 ml-1.5">{d.shiftName}</span>}
                        </span>
                        <span className={d.hours > 0 ? 'font-mono text-morandi-600' : 'text-morandi-300'}>
                          {d.hours > 0
                            ? `${d.hours} 小時`
                            : d.source === 'rest' ? '休息日，不計' : '0 小時'}
                          {d.source === 'default' && d.hours > 0 && (
                            <span className="text-morandi-300 ml-1">（未排班，以 8 小時計）</span>
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-sm text-morandi-400">無法估算時數，請確認日期</div>
              )}
            </div>
          )}

          <div className="flex justify-end mt-4">
            <button type="submit" disabled={submitting || estimating || (estimate?.hours ?? 0) <= 0}
              className="px-6 py-2.5 bg-smoke-500 text-white rounded-btn font-medium hover:bg-smoke-600 transition-colors min-h-touch">
              {submitting ? '送出中...' : '送出申請'}
            </button>
          </div>
        </form>
      )}

      {/* 歷史紀錄 */}
      <div className="flex-1 space-y-2 overflow-auto scrollbar-hide">
        {records.map((leave) => {
          const status = statusMap[leave.status] ?? statusMap.PENDING;
          return (
            <div key={leave.id} className="flex items-center justify-between bg-white rounded-card shadow-card p-4">
              <div className="flex items-center gap-3">
                <span className={`badge ${leave.leaveType === 'ANNUAL' ? 'badge-success' : 'badge-info'}`}>
                  {leaveTypeLabels[leave.leaveType] ?? leave.leaveType}
                </span>
                <div>
                  <div className="text-sm text-morandi-700">
                    {leave.startDate.slice(0, 10)}{leave.startDate.slice(0, 10) !== leave.endDate.slice(0, 10) ? ` ~ ${leave.endDate.slice(0, 10)}` : ''}
                  </div>
                  <div className="text-xs text-morandi-300">{leave.hours} 小時</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`badge ${status.style}`}>{status.label}</span>
                {leave.status === 'PENDING' && (
                  <button
                    onClick={() => handleCancel(leave)}
                    disabled={cancellingId === leave.id}
                    className="px-3 py-1.5 text-xs rounded-btn border border-morandi-200 text-morandi-500 hover:bg-morandi-50 disabled:opacity-50 transition-colors"
                  >
                    {cancellingId === leave.id ? '取消中…' : '取消申請'}
                  </button>
                )}
              </div>
            </div>
          );
        })}
        {records.length === 0 && (
          <div className="text-center text-morandi-300 text-sm py-10">尚無請假紀錄</div>
        )}
      </div>
    </div>
  );
}
